// Phase 17 — /admin/operators: list operators + provision a new one.
//
// Creating an operator is privilege-granting, so the backend POST is behind
// TotpFreshGuard. The mutation surfaces TotpStepUpRequiredError, which the
// global MutationCache→TotpStepUpModalProvider bridge (main.tsx) catches,
// prompts for a fresh TOTP, and replays — so this page needs no per-action
// step-up wiring, just a plain useMutation. The new operator receives a
// magic-link and must enroll TOTP at /setup-2fa on first sign-in.

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, TotpStepUpRequiredError, unwrap } from '@truecare/api-client';
import { api } from '../api';
import { AdminLayout } from '../components/AdminLayout';
import { AdminTable, type AdminTableColumn } from '../components/AdminTable';
import { DangerButton } from '../components/DangerButton';
import { adminBeforeLoad } from '../lib/admin-before-load';

export const Route = createFileRoute('/operators')({
  beforeLoad: () => adminBeforeLoad('/operators'),
  component: OperatorsPage,
});

interface OperatorRow {
  id: string;
  email: string;
  displayName: string | null;
  status: string;
  totpEnrolled: boolean;
  createdAt: string;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function OperatorsPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin', 'operators'],
    queryFn: async () =>
      (await unwrap(api.GET('/api/v1/admin/operators'))) as { items: OperatorRow[] },
  });

  // Current operator's id — used to hide the Deactivate button on their own
  // row (mirrors the backend's cannot_deactivate_self guard).
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => (await unwrap(api.GET('/api/v1/auth/me'))) as { id: string },
  });
  const currentUserId = meQuery.data?.id;

  const createMutation = useMutation({
    mutationFn: async () =>
      unwrap(
        api.POST('/api/v1/admin/operators', {
          body: { email: email.trim(), displayName: displayName.trim() },
        }),
      ),
    onSuccess: () => {
      setNotice(
        `Invite sent to ${email.trim()} — they'll get a magic-link to sign in, and must set up two-factor authentication on first login.`,
      );
      setEmail('');
      setDisplayName('');
      void qc.invalidateQueries({ queryKey: ['admin', 'operators'] });
    },
  });

  // Deactivate: revoke the OPERATOR role + cut sessions. TotpFresh-gated on the
  // backend; the global step-up bridge handles the replay. onConfirm throws on
  // error so the DangerButton modal surfaces the 409 (last_operator, etc.).
  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/api/v1/admin/operators/{id}/deactivate', {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => {
      setNotice(
        'Operator deactivated — their admin access is revoked and all their sessions were ended.',
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'operators'] });
    },
  });

  const columns: AdminTableColumn<OperatorRow>[] = [
    { key: 'email', header: 'Email' },
    { key: 'displayName', header: 'Name', sortable: false, render: (r) => r.displayName ?? '—' },
    { key: 'status', header: 'Status', width: 'w-28' },
    {
      key: 'totpEnrolled',
      header: '2FA',
      width: 'w-28',
      render: (r) => (r.totpEnrolled ? 'Enrolled' : 'Pending'),
    },
    {
      key: 'createdAt',
      header: 'Added',
      width: 'w-40',
      sortable: false,
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      width: 'w-40',
      render: (r) =>
        // Audit #19 (2026-09-01) — while meQuery is pending, currentUserId is
        // undefined, so the own-row check below was false and "Deactivate"
        // briefly rendered on the operator's OWN row. Hold destructive actions
        // until we know who's signed in. (The backend cannot_deactivate_self
        // guard already prevents lockout; this removes the confusing flash.)
        currentUserId === undefined ? (
          <span className="text-xs text-ink-muted">…</span>
        ) : r.id === currentUserId ? (
          <span className="text-xs text-ink-muted">You</span>
        ) : (
          <DangerButton
            entityName={r.email}
            actionLabel="Deactivate operator"
            confirmationToken={r.email}
            requireReason={false}
            onConfirm={async () => {
              await deactivateMutation.mutateAsync(r.id);
            }}
            data-testid={`operator-deactivate-${r.id}`}
          >
            Deactivate
          </DangerButton>
        ),
    },
  ];

  const canSubmit =
    EMAIL_RE.test(email.trim()) && displayName.trim().length > 0 && !createMutation.isPending;

  // TotpStepUpRequiredError is handled globally (modal + replay) — never surface it.
  const showError =
    createMutation.isError && !(createMutation.error instanceof TotpStepUpRequiredError);
  const errorText = (() => {
    const e = createMutation.error;
    if (e instanceof ApiError) {
      if (e.status === 409) return 'That email is already in use by another account.';
      return e.message;
    }
    return 'Something went wrong. Please try again.';
  })();

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <h1 className="font-heading text-3xl text-forest md:text-4xl">Operators</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Everyone with operator (admin) access. Adding one emails them a
            magic-link to sign in; they must set up two-factor authentication on
            first login.
          </p>
        </header>

        <section className="rounded-lg border border-border bg-paper p-5 shadow-sm">
          <h2 className="font-heading text-lg text-forest">Add operator</h2>
          <form
            className="mt-4 flex flex-col gap-4 md:flex-row md:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              setNotice(null);
              createMutation.mutate();
            }}
          >
            <label className="flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@truecarepartners.ca"
                className="mt-1 block w-full rounded-md border border-border bg-paper px-3 py-2 text-sm text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
                data-testid="operator-email"
              />
            </label>
            <label className="flex-1">
              <span className="block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                Display name
              </span>
              <input
                value={displayName}
                maxLength={120}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jordan Operator"
                className="mt-1 block w-full rounded-md border border-border bg-paper px-3 py-2 text-sm text-ink focus:border-forest focus:outline-none focus:ring-1 focus:ring-forest"
                data-testid="operator-display-name"
              />
            </label>
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-forest px-5 py-2 text-sm font-semibold text-paper transition hover:bg-forest/90 disabled:cursor-not-allowed disabled:opacity-60"
              data-testid="operator-add"
            >
              {createMutation.isPending ? 'Adding…' : 'Add operator'}
            </button>
          </form>
          {notice && (
            <p
              className="mt-3 rounded-md border border-success/30 bg-success/5 px-3 py-2 text-sm text-forest"
              data-testid="operator-notice"
            >
              {notice}
            </p>
          )}
          {showError && (
            <p className="mt-3 rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-sm text-danger">
              {errorText}
            </p>
          )}
        </section>

        <AdminTable
          columns={columns}
          rows={listQuery.data?.items ?? []}
          isLoading={listQuery.isPending}
          isError={listQuery.isError}
          emptyMessage="No operators yet."
          page={1}
          limit={100}
          totalCount={listQuery.data?.items.length ?? 0}
          onPageChange={() => undefined}
          onLimitChange={() => undefined}
        />
      </div>
    </AdminLayout>
  );
}
